import { Component, computed, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { PreloaderColor, UIInputStyle } from '@enums/shared.enum';
import { SharedSelectors } from '@states/shared/shared.selector-types';
import { features } from '@consts/text.const';
import { SharedActions } from '@states/shared/shared.action-types';
import { Router } from '@angular/router';
import { CustomEventModel } from '@models/custom-event.model';
import { CustomEventsSelectors } from '@states/custom-events/custom-events.selector-types';
import { CustomEventsActions } from '@states/custom-events/custom-events.action-types';
import { routerSegments } from '@consts/routes';
import * as MenuActions from '@states/menu/menu.actions';
import { MenuKey } from '@enums/menu.enum';
import { CustomEventsEffect } from '@effects/custom-events.effect';
import { UiKitModule } from '../../../../shared/ui-kit/ui-kit.module';
import { TranslatePipe } from '@ngx-translate/core';
import { syncQueryParam } from '../../../../helpers/sync-query-param.helper';
import { UiSearchComponent } from '../../../../shared/ui-kit/ui-search/ui-search.component';
import { UiNoDataComponent } from '@ui';
import { UiPreloaderComponent } from '../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createCustomEventsTableConfig } from './custom-events-table.util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type ViewState = 'LOADING' | 'HAS_DATA' | 'NO_RESULTS' | 'INITIAL_EMPTY';

@Component({
  selector: 'app-custom-events',
  templateUrl: './custom-events.component.html',
  styleUrls: ['./custom-events.component.scss'],
  imports: [
    UiKitModule,
    TranslatePipe,
    UiSearchComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiDatasetTableComponent,
  ],
})
export class CustomEventsComponent implements OnInit, OnDestroy {
  public UIInputStyle = UIInputStyle;

  public isLoading = this.store$.selectSignal(SharedSelectors.selectIsLoading);
  public selectAllCustomEvents = this.store$.selectSignal(CustomEventsSelectors.selectAllCustomEvents);

  public search = syncQueryParam<string>('search');

  public readonly tableConfig = computed(() => {
    return createCustomEventsTableConfig();
  });

  public readonly noData = features.customEvents.noData;
  public readonly noResults = features.customEvents.noResults;
  public readonly PreloaderColor = PreloaderColor;

  public readonly vm$ = computed(() => {
    const isLoading = this.isLoading();
    const allEvents = this.selectAllCustomEvents();
    const search = this.search();

    const filteredEvents = !search
      ? allEvents
      : allEvents.filter((p) => p?.name?.toLowerCase().includes(search.toLowerCase()));

    let viewState: ViewState;
    if (isLoading && !allEvents.length) {
      viewState = 'LOADING';
    } else if (allEvents.length > 0 && search && !filteredEvents.length) {
      viewState = 'NO_RESULTS';
    } else if (allEvents.length > 0) {
      viewState = 'HAS_DATA';
    } else {
      viewState = 'INITIAL_EMPTY';
    }

    return {
      viewState,
      filteredEvents,
      allEvents,
      search,
    };
  });

  constructor(
    private store$: Store,
    private customEventsEffect: CustomEventsEffect,
    private router: Router,
    private destroyRef: DestroyRef,
  ) {}

  public ngOnInit(): void {
    this.store$.dispatch(
      MenuActions.setActiveLevel2Menu({
        activeLevel2: MenuKey.eventType,
      }),
    );
    this.store$.dispatch(SharedActions.setIsLoading({ isLoading: true }));
    this.store$.dispatch(CustomEventsActions.getCustomEvents());

    this.customEventsEffect.pressSave$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.router.navigate([routerSegments.database, routerSegments.eventTypes, routerSegments.create]);
    });
  }

  public onRowAction(event: {
    actionId: string;
    row: CustomEventModel.CustomEvent;
  }): void {
    if (event.actionId === 'edit') {
      this.editCustomEvent(event.row);
    } else if (event.actionId === 'delete') {
      this.remove(event.row);
    }
  }

  public editCustomEvent(row: CustomEventModel.CustomEvent): void {
    this.router.navigate([routerSegments.database, routerSegments.eventTypes, routerSegments.edit, row._id]);
  }

  public remove(row: CustomEventModel.CustomEvent): void {
    this.store$.dispatch(CustomEventsActions.removeCustomEvent({ customEvent: row }));
  }

  public ngOnDestroy(): void {
    this.store$.dispatch(CustomEventsActions.resetToInitialState());
  }

  public createEventType() {
    this.store$.dispatch(CustomEventsActions.pressSave());
  }
}
